import { TRequiresTimeChange } from '@/types/session.types';
import { getDateOptionLabel } from './get-date-option-label';
import { getFirstPossibleCalcMinutes } from './get-first-possibible-calc-minutes';
import { getIterationStart } from './get-iteration-start';
import { getNumberOfOptions } from './get-number-of-options';
import { getTimeDiff } from './get-time-diff';
import { getTimezoneOffsetForDateMilliseconds, isSameDayInTimezone } from './get-timezone-offset-milliseconds';
import timezoneMap from '../timezone-short-name-map.json' with { type: 'json' };

const NO_TIME_DIFF = {
  daysDiff: null,
  hoursDiff: null,
  minutesDiff: null,
};

export const getDateOptionsDetail = (
  startDate: Date | null,
  endDate: Date | null,
  timeZone: keyof typeof timezoneMap = 'America/Los_Angeles',
  isEnd: boolean,
  generatedOptionIncrementMinutes: number = 30,
  requiresTimeChange?: TRequiresTimeChange | null | undefined
): {
  value: Date;
  label: string;
  daysDiff: number | null;
  hoursDiff: number | null;
  minutesDiff: number | null;
}[] => {
  // POSSIBLE OUTCOMES:
  // 1. we are generating endDate options
  //    A. the startDate and endDate are the SAME DAY when adjusted for the timezone: first option is startDate + generatedOptionIncrementMinutes
  //    B. the startDate and endDate are NOT SAME DAY when adjusted for the timezone: first option is the earliest option of the endDate as if every option between the two dates were generated
  // 2. we are generating startDate options
  //    A. the start date is TODAY
  //        i. the startDate has not been set and is the default: first option will be startDate + generatedOptionIncrementMinutes
  //        ii. the startDate has been set previously: all options for the startDate are possible starting at 00:00
  //    B. the start date is NOT TODAY
  //        i. the startDate has been set previously: all options for the startDate are possible starting at 00:00

  // bail out if no startDate, nothing can be calculated
  if (!startDate) return [];

  const dateOptionsDetail: {
    value: Date;
    label: string;
    daysDiff: number | null;
    hoursDiff: number | null;
    minutesDiff: number | null;
  }[] = [];

  const startDateNumeric = startDate.getTime();
  const millisecondsTimezoneOffset = getTimezoneOffsetForDateMilliseconds(startDateNumeric, timeZone);

  //if we do not have an endDate, this is a same day event clicked once, just use the startDate to calculate the options
  const endDateNumeric = endDate ? endDate.getTime() : startDate.getTime();

  const isStartAndEndSameDay = isSameDayInTimezone(startDateNumeric, endDateNumeric, timeZone);

  const firstPossibleCalcMinutes = getFirstPossibleCalcMinutes(
    isEnd,
    isStartAndEndSameDay,
    startDateNumeric,
    millisecondsTimezoneOffset,
    requiresTimeChange,
    generatedOptionIncrementMinutes,
    timeZone
  );

  const iterationDate = getIterationStart(
    isEnd,
    isStartAndEndSameDay,
    startDateNumeric,
    endDateNumeric,
    firstPossibleCalcMinutes,
    millisecondsTimezoneOffset
  );

  const numberOfOptions = getNumberOfOptions(firstPossibleCalcMinutes, generatedOptionIncrementMinutes);

  // in the case that we are dealing with an endDate AND the start and end are different days, we want the unaltered iterationDate as an option
  // prime the output dateOptionsDetail with the value of iteration
  if (
    (isEnd && !isStartAndEndSameDay) || // if we are calculating endDate options AND we are not on the same day
    !isEnd // we are generating startDate options
  ) {
    // Recalculate offset for the iteration date to handle DST transitions correctly
    const iterationDateOffset = getTimezoneOffsetForDateMilliseconds(iterationDate, timeZone);
    dateOptionsDetail.push({
      value: new Date(iterationDate - iterationDateOffset),
      label: getDateOptionLabel(iterationDate),
      ...(isEnd ? getTimeDiff(startDateNumeric, iterationDate - iterationDateOffset) : NO_TIME_DIFF),
    });
  }

  let iterator = 1;

  for (iterator; iterator < numberOfOptions; iterator++) {
    const dateOptionNumeric = iterationDate + iterator * 60000 * generatedOptionIncrementMinutes;
    // Recalculate offset for each date option to handle DST transitions correctly
    const dateOptionOffset = getTimezoneOffsetForDateMilliseconds(dateOptionNumeric, timeZone);

    dateOptionsDetail.push({
      value: new Date(dateOptionNumeric - dateOptionOffset),
      label: getDateOptionLabel(dateOptionNumeric),
      ...(isEnd ? getTimeDiff(startDateNumeric, dateOptionNumeric - dateOptionOffset) : NO_TIME_DIFF),
    });
  }

  return dateOptionsDetail;
};
